import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { preventDoubleClick } from "@/lib/dblclick-event-guard"

const buttonVariants = cva(
  "transition-all duration-150 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-[color,box-shadow] duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-accent-600 text-white shadow-xs hover:bg-accent-600/90",
        atrium:
          "bg-accent-600 cursor-pointer text-white shadow-black hover:bg-accent-600/90 h-11 rounded-lg transition-all duration-150 focus:ring-4 focus:ring-accent-200 focus:ring-offset-0",
        white:
          "border border-atrium-border bg-white shadow-xs text-atrium-black hover:bg-atrium-content hover:text-atrium-black hover:border-atrium-border hover:drop-shadow-sm focus:ring-2 focus:ring-accent-200 focus:ring-offset-0 dark:bg-atrium-inner dark:hover:bg-atrium-content dark:border-atrium-border dark:hover:border-atrium-border-dark",
        black:
          "bg-atrium-black text-white shadow-xs hover:bg-atrium-black/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "bg-atrium-background shadow-none hover:drop-shadow-xs cursor-pointer border border-atrium-border text-atrium-black hover:bg-atrium-content hover:text-atrium-black hover:border-atrium-border hover:drop-shadow-sm focus:ring-2 focus:ring-accent-600 focus:ring-offset-0 focus:border-accent-600 dark:hover:bg-atrium-inner dark:focus:ring-accent-500",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-atrium-content cursor-pointer dark:hover:bg-atrium-inner",
        link: "text-accent-600 underline-offset-4 hover:underline",
        /** Circular soft ring + inset highlight + gradient rim; icon/content are direct children (after rim). Size defaults to `chrome` (circle); use `chromeWide` for a horizontal pill. */
        chrome:
          "group cursor-pointer relative bg-white text-atrium-black font-normal whitespace-normal shadow-[0_1px_2px_rgba(38,37,30,0.06),0_0_0_3px_var(--color-warm-2),inset_0_-2px_0_rgba(38,37,30,0.05)] transition-[box-shadow,transform] duration-150 hover:shadow-[0_1px_3px_rgba(38,37,30,0.08),0_0_0_3px_var(--color-warm-2),inset_0_-1px_0_rgba(38,37,30,0.06)] active:shadow-[0_1px_1px_rgba(38,37,30,0.05),0_0_0_3px_var(--color-warm-2),inset_0_0_1px_rgba(38,37,30,0.08)] active:scale-[0.98] focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2 [&>*:not([data-slot=chrome-rim])]:relative [&>*:not([data-slot=chrome-rim])]:z-10 [&_svg]:shrink-0",
        /** Same structure as `chrome` with atrium-accent face, white label, and accent-tinted rim/shadows. */
        chromeAccent:
          "group cursor-pointer relative bg-accent-600 text-white font-normal whitespace-normal shadow-[0_1px_2px_rgba(0,40,25,0.22),0_0_0_3px_var(--color-accent-700),inset_0_-2px_0_rgba(157,209,187,0.22)] transition-[box-shadow,transform] duration-150 hover:shadow-[0_1px_3px_rgba(0,40,25,0.3),0_0_0_3px_var(--color-accent-800),inset_0_-1px_0_rgba(157,209,187,0.28)] active:shadow-[0_1px_1px_rgba(0,40,25,0.2),0_0_0_3px_var(--color-accent-700),inset_0_0_1px_rgba(0,40,25,0.35)] active:scale-[0.98] focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-accent-200 focus-visible:ring-offset-2 [&>*:not([data-slot=chrome-rim])]:relative [&>*:not([data-slot=chrome-rim])]:z-10 [&_svg]:shrink-0",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        chrome:
          "size-[2.375rem] min-h-[2.375rem] min-w-[2.375rem] gap-0 rounded-full p-0",
        /** Same chrome styling as `chrome` / `chromeAccent` but grows horizontally (pill). */
        chromeWide:
          "h-10 min-h-10 min-w-0 w-auto max-w-full shrink justify-start gap-2 rounded-full px-2.5 py-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  children,
  onClick,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"
  const isChrome = variant === "chrome" || variant === "chromeAccent"
  const resolvedSize = isChrome ? (size ?? "chrome") : size

  const chromeRim =
    isChrome && !asChild ? (
      variant === "chromeAccent" ? (
        <span
          data-slot="chrome-rim"
          aria-hidden
          className="pointer-events-none absolute w-full -inset-px z-0 rounded-[inherit] bg-linear-to-b from-accent-200/35 to-accent-600/90 p-px group-active:from-accent-300/28 group-active:to-accent-700/82"
        >
          <span className="block size-full rounded-[inherit] bg-accent-600 shadow-[inset_0_-2px_0_rgba(194,227,213,0.38)] group-hover:shadow-[inset_0_-1px_0_rgba(194,227,213,0.34)]" />
        </span>
      ) : (
        <span
          data-slot="chrome-rim"
          aria-hidden
          className="pointer-events-none absolute w-full -inset-px z-0 rounded-[inherit] bg-linear-to-b from-atrium-black/[0.05] to-atrium-black/[0.14] p-px group-active:from-atrium-black/[0.08] group-active:to-atrium-black/[0.1]"
        >
          <span className="block size-full rounded-[inherit] bg-white shadow-[inset_0_-2px_0_rgba(235,235,230,0.6)] group-hover:shadow-[inset_0_-1px_0_rgba(235,235,230,0.6)]" />
        </span>
      )
    ) : null

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size: resolvedSize }), className)}
      onClick={preventDoubleClick(onClick)}
      {...props}
    >
      {asChild ? children : (
        <>
          {chromeRim}
          {children}
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
